import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DevelopmentApproach = () => {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      id: 'consultation',
      title: 'Discovery & Consultation',
      icon: 'MessageSquare',
      duration: '1-2 weeks',
      description: 'We start by understanding your vision, goals, and constraints through detailed consultations.',
      details: `Our discovery phase is comprehensive yet efficient. We conduct stakeholder interviews, analyze your current digital presence, and identify opportunities for improvement. This phase includes competitive analysis, user persona development, and technical requirements gathering. We believe that a strong foundation leads to exceptional results.`,
      deliverables: [
        'Project requirements document',
        'Technical architecture plan',
        'User experience wireframes',
        'Timeline and milestone roadmap'
      ],
      tools: ['Figma', 'Miro', 'Notion', 'Zoom']
    },
    {
      id: 'planning',
      title: 'Strategic Planning',
      icon: 'Layout',
      duration: '1 week',
      description: 'Detailed project planning with clear milestones, timelines, and resource allocation.',
      details: `Strategic planning transforms your vision into actionable steps. We create detailed project roadmaps, define technical specifications, and establish clear communication protocols. Our planning phase includes risk assessment, resource allocation, and contingency planning to ensure smooth project execution.`,
      deliverables: [
        'Detailed project timeline',
        'Resource allocation plan',
        'Risk assessment matrix',
        'Communication framework'
      ],
      tools: ['Jira', 'Slack', 'GitHub', 'Calendly']
    },
    {
      id: 'design',
      title: 'Design & Prototyping',
      icon: 'Palette',
      duration: '2-3 weeks',
      description: 'Creating intuitive, beautiful designs that align with your brand and user needs.',
      details: `Our design process balances aesthetics with functionality. We create user-centered designs that not only look stunning but also drive conversions and engagement. Every design decision is backed by user research and best practices, ensuring your digital presence stands out in the market.`,
      deliverables: [
        'High-fidelity mockups',
        'Interactive prototypes',
        'Design system documentation',
        'Brand guideline integration'
      ],
      tools: ['Figma', 'Adobe Creative Suite', 'Principle', 'InVision']
    },
    {
      id: 'development',
      title: 'Development & Testing',
      icon: 'Code',
      duration: '4-8 weeks',
      description: 'Building robust, scalable solutions using cutting-edge technologies and best practices.',
      details: `Development is where your vision comes to life. We follow agile methodologies with regular check-ins and demos. Our code is clean, well-documented, and thoroughly tested. We implement continuous integration and deployment practices to ensure quality and reliability throughout the development process.`,
      deliverables: [
        'Fully functional application',
        'Comprehensive test coverage',
        'Performance optimization',
        'Security implementation'
      ],
      tools: ['React', 'Node.js', 'AWS', 'Docker']
    },
    {
      id: 'deployment',
      title: 'Deployment & Launch',
      icon: 'Rocket',
      duration: '1 week',
      description: 'Seamless deployment with monitoring, optimization, and launch support.',
      details: `Launch day is just the beginning. We handle all technical aspects of deployment, including server setup, domain configuration, and performance monitoring. Our deployment process includes thorough testing in production environments and immediate post-launch support to ensure everything runs smoothly.`,
      deliverables: [
        'Production deployment',
        'Performance monitoring setup',
        'Analytics implementation',
        'Launch day support'
      ],
      tools: ['AWS', 'Vercel', 'Google Analytics', 'Sentry']
    },
    {
      id: 'support',
      title: 'Ongoing Support',
      icon: 'Shield',
      duration: 'Ongoing',
      description: 'Continuous support, maintenance, and optimization to ensure long-term success.',
      details: `Our relationship doesn't end at launch. We provide ongoing support, regular updates, and continuous optimization. This includes security updates, performance monitoring, feature enhancements, and strategic guidance as your business grows and evolves.`,
      deliverables: [
        '24/7 monitoring and support','Regular security updates','Performance optimization','Strategic consultation'
      ],
      tools: ['Monitoring Tools', 'Support Portal', 'Update Systems', 'Analytics']
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Settings" size={16} />
            <span>Our Approach</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            How We Build Excellence
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our proven development methodology ensures every project is delivered on time, 
            within budget, and exceeds expectations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Phase Navigation */}
          <div className="space-y-4">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <button
                  onClick={() => setActivePhase(index)}
                  className={`w-full text-left p-6 rounded-xl border transition-all duration-300 ${
                    activePhase === index
                      ? 'bg-primary/10 border-primary shadow-brand'
                      : 'bg-card border-border hover:border-primary/50 hover:shadow-lg'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                      activePhase === index
                        ? 'bg-primary text-white' :'bg-muted text-text-secondary'
                    }`}>
                      <Icon name={phase.icon} size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`font-semibold transition-colors duration-300 ${
                          activePhase === index ? 'text-primary' : 'text-foreground'
                        }`}>
                          {phase.title}
                        </h3>
                        <span className="text-sm text-text-secondary">{phase.duration}</span>
                      </div>
                      <p className="text-sm text-text-secondary">{phase.description}</p>
                    </div>
                    <Icon 
                      name="ChevronRight" 
                      size={20} 
                      className={`transition-colors duration-300 ${
                        activePhase === index ? 'text-primary' : 'text-text-secondary'
                      }`}
                    />
                  </div>
                </button>
              </motion.div>
            ))}
          </div>

          {/* Phase Details */}
          <div className="lg:sticky lg:top-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activePhase}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card border border-border rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                    <Icon name={phases[activePhase].icon} size={24} color="white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">{phases[activePhase].title}</h3>
                    <p className="text-primary font-medium">{phases[activePhase].duration}</p>
                  </div>
                </div>

                <p className="text-text-secondary mb-6 leading-relaxed">
                  {phases[activePhase].details}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-foreground mb-4">Key Deliverables:</h4>
                  <div className="space-y-2">
                    {phases[activePhase].deliverables.map((deliverable, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                        <span className="text-sm text-text-secondary">{deliverable}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-4">Tools & Technologies:</h4>
                  <div className="flex flex-wrap gap-2">
                    {phases[activePhase].tools.map((tool, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-muted text-text-secondary text-sm rounded-full"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Process Visualization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">
              Seamless Project Flow
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
              {phases.map((phase, index) => (
                <React.Fragment key={phase.id}>
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-2">
                      <Icon name={phase.icon} size={16} color="white" />
                    </div>
                    <span className="text-xs text-text-secondary text-center max-w-20">
                      {phase.title.split(' ')[0]}
                    </span>
                  </div>
                  {index < phases.length - 1 && (
                    <Icon name="ArrowRight" size={16} className="text-text-secondary hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DevelopmentApproach;